import type { WorkoutDetail } from '../services/api';
import type { StreamPoint } from './workoutStream';
import { workoutEnvironmentRanges, type SensorRange } from './workoutEnvironmentRanges';
import { isRunningWorkout } from './runningWorkoutMetrics';
import { runningStatsPresentation } from './runningStatsPresentation';

const parts = ['min', 'avg', 'max'] as const;
const number = (value: unknown): number | null => {
  if (value == null || (typeof value !== 'number' && typeof value !== 'string') || (typeof value === 'string' && !value.trim())) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const merge = (...sources: any[]): SensorRange => Object.fromEntries(parts.map(part => [part,
  sources.map(source => number(source?.[part])).find(value => value != null) ?? null,
])) as SensorRange;

/** Whole-workout statistics. Missing sensors remain missing; signed environmental values are valid. */
export function analysisSummaryStats(workout: WorkoutDetail, stream: StreamPoint[] = []): Record<string, SensorRange | undefined> {
  const mma = workout.details?.min_max_avg as Record<string, any> | undefined;
  const environment = workoutEnvironmentRanges(workout.details?.fit_metrics, stream);
  const result: Record<string, SensorRange | undefined> = {};
  for (const key of ['power', 'heart_rate', 'cadence', 'speed']) if (mma?.[key]) result[key] = merge(mma[key]);
  result.altitude = merge(mma?.altitude, mma?.enhanced_altitude, mma?.elevation, environment.altitude);
  result.temperature = merge(mma?.temperature, environment.temperature, workout.running_metrics?.temperature);
  if (isRunningWorkout(workout)) {
    const running = runningStatsPresentation(workout);
    result.heart_rate = merge(running.heartRate);
    result.cadence = merge(running.cadence);
    if (running.power) result.power = merge(running.power);
    result.speed = merge(mma?.speed, running.speed);
    const pace = (speed: number | null) => speed != null && speed > 0 ? 3600 / speed : null;
    result.pace = { min: pace(result.speed.max), avg: running.pace, max: pace(result.speed.min) };
  }
  return result;
}
